import { AnimatePresence, motion } from 'framer-motion';
import { lazy, Suspense, useEffect } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import Login from './components/auth/Login';
import ProtectedRoute from './components/auth/ProtectedRoute';
import SsoCallback from './components/auth/SsoCallback';
import ErrorBoundary from './components/common/ErrorBoundary';
import { useAuth } from './providers/AuthProvider';
import { useTheme } from './providers/ThemeProvider';

const DashboardLayout = lazy(() => import('./components/layout/DashboardLayout'));
const Dashboard = lazy(() => import('./components/dashboard/Dashboard'));
const LogsViewer = lazy(() => import('./components/logs/LogsViewer'));
const Signup = lazy(() => import('./components/auth/Signup'));
const Profile = lazy(() => import('./components/profile/Profile'));
const UsersManagement = lazy(() => import('./components/admin/UsersManagement'));

const PageSpinner = () => (
  <div className="h-screen bg-gray-50 flex items-center justify-center">
    <div className="text-center">
      <div className="spinner mx-auto mb-4"></div>
      <p className="text-gray-600">Chargement...</p>
    </div>
  </div>
);

function App() {
  const { isAuthenticated, loading, user } = useAuth();
  const { applyServerTheme } = useTheme();

  // Applique la préférence thème DB de l'utilisateur au login / refresh session
  useEffect(() => {
    if (user?.theme_preference && user.theme_preference !== 'system') {
      applyServerTheme(user.theme_preference);
    }
  }, [user?.theme_preference, applyServerTheme]);

  if (loading) {
    return <PageSpinner />;
  }

  return (
    <div className="min-h-screen bg-[color:var(--surface-0)] text-[color:var(--text-primary)]">
      <AnimatePresence mode="wait">
        <Routes>
          <Route
            path="/login"
            element={
              isAuthenticated ? (
                <Navigate to="/dashboard" replace />
              ) : (
                <ErrorBoundary>
                  <motion.div
                    key="login"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -20 }}
                    transition={{ duration: 0.3 }}
                  >
                    <Login />
                  </motion.div>
                </ErrorBoundary>
              )
            }
          />

          <Route
            path="/signup"
            element={
              isAuthenticated ? (
                <Navigate to="/dashboard" replace />
              ) : (
                <ErrorBoundary>
                  <Suspense fallback={<PageSpinner />}>
                    <motion.div
                      key="signup"
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -20 }}
                      transition={{ duration: 0.3 }}
                    >
                      <Signup />
                    </motion.div>
                  </Suspense>
                </ErrorBoundary>
              )
            }
          />

          {/* Routes authentifiées partageant le layout (Sidebar + Header) */}
          <Route
            element={
              <ProtectedRoute>
                <ErrorBoundary>
                  <Suspense fallback={<PageSpinner />}>
                    <DashboardLayout />
                  </Suspense>
                </ErrorBoundary>
              </ProtectedRoute>
            }
          >
            <Route
              path="/dashboard/*"
              element={
                <Suspense fallback={<PageSpinner />}>
                  <Dashboard />
                </Suspense>
              }
            />
            <Route
              path="/profile"
              element={
                <Suspense fallback={<PageSpinner />}>
                  <Profile />
                </Suspense>
              }
            />
            <Route
              path="/admin/users"
              element={
                <Suspense fallback={<PageSpinner />}>
                  <UsersManagement />
                </Suspense>
              }
            />
          </Route>

          {/* LogsViewer : sans layout (full-screen pour max d'espace logs) */}
          <Route
            path="/logs/:ip/:port"
            element={
              <ProtectedRoute>
                <ErrorBoundary>
                  <Suspense fallback={<PageSpinner />}>
                    <motion.div
                      key="logs"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      transition={{ duration: 0.3 }}
                    >
                      <LogsViewer />
                    </motion.div>
                  </Suspense>
                </ErrorBoundary>
              </ProtectedRoute>
            }
          />

          {/* Callback SSO Atlassian — route publique */}
          <Route
            path="/sso-callback"
            element={
              <ErrorBoundary>
                <SsoCallback />
              </ErrorBoundary>
            }
          />

          <Route path="/" element={<Navigate to="/dashboard" replace />} />

          <Route
            path="*"
            element={
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="h-screen flex items-center justify-center bg-gray-50"
              >
                <div className="text-center">
                  <h1 className="text-6xl font-bold text-gray-900 mb-4">404</h1>
                  <p className="text-xl text-gray-600 mb-8">Page non trouvée</p>
                  <button
                    type="button"
                    onClick={() => window.history.back()}
                    className="btn-primary"
                  >
                    Retour
                  </button>
                </div>
              </motion.div>
            }
          />
        </Routes>
      </AnimatePresence>
    </div>
  );
}

export default App;
